import { useCallback, useEffect, useRef, useState } from 'react'
import { Allotment } from 'allotment'
import Layout, { Footer } from 'antd/lib/layout/layout'
import Sider from 'antd/lib/layout/Sider'
import { SidebarItem, WorkbenchUiAccessorType } from 'Core/Workbench'
import { Sidebar, isDivider } from 'Components/common/Sidebar/Sidebar'
import { SidebarAccessorType, useSidebar } from 'Hooks/use-sidebar'
import { ActionsContainer } from 'Layouts/Workbench/ActionsContainer/ActionsContainer'
import { useToolbar } from 'Hooks/use-toolbar'
import { SidebarItemPanel } from 'Layouts/Workbench/SidebarItemPanel/SidebarItemPanel'
import 'allotment/dist/style.css'
import classes from './WorkbenchLayout.module.less'
import App from 'antd/lib/app'
import HeaderComp from 'Components/common/Header/Header'
import { useUiState } from 'Hooks/use-ui-state'
import { ContainerType, DecisionStatusType, TaskConfigType } from 'Types/Tasks.types'
import { InformationContainer } from './InformationElements/InformationContainer'
import { useForceUpdate } from 'Hooks/use-force-update'
import { useNotificationBadge } from 'Hooks/use-notification-badge'
import {
    EXPERTS_THOUGHTS_ITEM_ID,
    TASK_INSTRUCTIONS_ITEM_ID,
    SUPPLEMENTARY_DETAILS_ITEM_ID,
} from 'Constants/constants'
import { useModalContainer } from 'Hooks/use-modal-container'
import { TaskContextProvider, TaskContextType } from 'Contexts/TaskContext'

export type WorkbenchLayoutPropsType = {
    setAccessor: (accessor: WorkbenchUiAccessorType) => void
    taskConfig: TaskConfigType<ContainerType, unknown>
}

export type WorkbenchLayoutStateType = {
    widths: number[]
    leftActiveItem: string | null | undefined
    rightActiveItem: string | null | undefined
}

export const WorkbenchLayout: React.FC<WorkbenchLayoutPropsType> = (
    props: WorkbenchLayoutPropsType
) => {
    const [initialized, setInitialized] = useState(false)
    const [mainContent, setMainContent] = useState<React.ReactElement>(<></>)
    const forceUpdate = useForceUpdate()
    const onRightSidebarActiveItemChangeRef = useRef<((itemId: string | undefined) => void) | null>(
        null
    )

    const notifier = App.useApp()

    const leftSidebar = useSidebar()
    const rightSidebar = useSidebar()
    const notificationBadge = useNotificationBadge(props.taskConfig.assignment.task.id)
    const toolbar = useToolbar()
    const modalContainer = useModalContainer()
    const [taskContextGetter, setTaskContextGetter] = useState<{
        getTaskContext: () => TaskContextType | undefined
    }>(() => ({ getTaskContext: () => undefined }))

    const { initialUiState, persistUiState } = useUiState()

    const addMainContent = useCallback((element: React.ReactElement) => {
        setMainContent(element)
    }, [])

    const [shouldCloseOppositeSidebar, setShouldCloseOppositeSidebar] = useState(false)

    useEffect(() => {
        props.setAccessor({
            leftSidebar,
            rightSidebar,
            replaceMainContent: addMainContent,
            toolbar,
            notifier,
            setInitialized,
            updateUI: forceUpdate,
            notificationBadge,
            modalContainer,
            setTaskContextGetter: setTaskContextGetter,
            setShouldCloseOppositeSidebar: setShouldCloseOppositeSidebar,
            setOnRightSidebarActiveItemChange: callback => {
                onRightSidebarActiveItemChangeRef.current = callback
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leftSidebar, rightSidebar, toolbar])

    useEffect(() => {
        if (!initialized || !leftSidebar || !rightSidebar || !initialUiState) return

        const updateSidebarState = (
            sidebar: SidebarAccessorType,
            activeItem: string | undefined
        ) => {
            sidebar.changeActiveItemId(() => {
                if (activeItem) {
                    notificationBadge.dismissNotificationBadge(activeItem)
                    return activeItem
                }
                return
            })
        }

        const determineLeftActiveItem = (): string | undefined => {
            const isRejectedTask =
                props.taskConfig.assignment.task.output_start_state_container?.decision?.status ===
                DecisionStatusType.REJECT
            const notification = notificationBadge.items.find(
                item => item.id === EXPERTS_THOUGHTS_ITEM_ID
            )

            if (isRejectedTask && notification) {
                return EXPERTS_THOUGHTS_ITEM_ID
            }
            if ('leftActiveItem' in initialUiState) {
                return initialUiState.leftActiveItem ?? undefined
            }
            return TASK_INSTRUCTIONS_ITEM_ID
        }

        const determineRightActiveItem = (): string | undefined => {
            if ('rightActiveItem' in initialUiState) {
                return initialUiState.rightActiveItem ?? undefined
            }
            return SUPPLEMENTARY_DETAILS_ITEM_ID
        }

        const leftActiveItem = determineLeftActiveItem()
        const rightActiveItem = determineRightActiveItem()
        updateSidebarState(leftSidebar, leftActiveItem)
        updateSidebarState(rightSidebar, rightActiveItem)
        onRightSidebarActiveItemChangeRef.current?.(rightActiveItem)
        persistUiState({
            leftActiveItem: leftActiveItem ?? null,
            rightActiveItem: rightActiveItem ?? null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized])

    if (!initialUiState) {
        return null
    }

    const taskContext = taskContextGetter.getTaskContext?.()
    if (!taskContext) {
        return null
    }

    return (
        <Layout data-testid="workbench-layout">
            <TaskContextProvider value={taskContext}>
                <HeaderComp>
                    <>
                        <InformationContainer infoElements={toolbar.infoElements} />
                        <div className={classes.topbarRight}>
                            <div
                                id="proctoring-header-slot"
                                className={classes.proctoringSlot}
                                data-testid="proctoring-header-slot"
                            />
                            <ActionsContainer
                                actions={toolbar.actions}
                                taskConfig={props.taskConfig}
                            />
                        </div>
                    </>
                </HeaderComp>
                <Layout>
                    <Sider width={48} className={classes.leftBar}>
                        <Sidebar
                            taskConfig={props.taskConfig}
                            sidebarItems={leftSidebar.items}
                            activeItem={leftSidebar.activeItemId}
                            toggleItem={itemId => {
                                leftSidebar.changeActiveItemId(current => {
                                    const activeItem = current === itemId ? undefined : itemId
                                    if (activeItem && shouldCloseOppositeSidebar) {
                                        rightSidebar.changeActiveItemId(() => undefined)
                                    }
                                    persistUiState({
                                        leftActiveItem: activeItem ?? null,
                                    })
                                    activeItem &&
                                        notificationBadge.dismissNotificationBadge(activeItem)
                                    return activeItem
                                })
                            }}
                            notificationBadges={notificationBadge.items}
                            placement="left"
                        />
                    </Sider>
                    <Allotment
                        onChange={sizes => persistUiState({ widths: sizes })}
                        vertical={false}
                        className={classes.allotmentContent}
                    >
                        <Allotment.Pane
                            minSize={260} //For now added this size so UI should not break
                            preferredSize={initialUiState?.widths?.[0] ?? '25%'}
                            maxSize={350}
                            className={classes.leftPanel}
                            visible={leftSidebar.activeItemId !== undefined}
                        >
                            <SidebarItemPanel
                                sidebarItems={
                                    leftSidebar.items.filter(
                                        item => !isDivider(item)
                                    ) as SidebarItem[]
                                }
                                activeItem={leftSidebar.activeItemId}
                            />
                        </Allotment.Pane>
                        <Allotment.Pane
                            preferredSize={initialUiState?.widths?.[1] ?? 600}
                            minSize={600}
                            className={classes.content}
                        >
                            <div className={classes.content} id="main-content">
                                {mainContent}
                            </div>
                        </Allotment.Pane>

                        <Allotment.Pane
                            minSize={260}
                            preferredSize={initialUiState?.widths?.[2] ?? '20%'}
                            maxSize={350}
                            className={classes.rightPanel}
                            visible={rightSidebar.activeItemId !== undefined}
                        >
                            <SidebarItemPanel
                                sidebarItems={
                                    rightSidebar.items.filter(
                                        item => !isDivider(item)
                                    ) as SidebarItem[]
                                }
                                activeItem={rightSidebar.activeItemId}
                            />
                        </Allotment.Pane>
                    </Allotment>
                    <Sider width={48} className={classes.rightBar}>
                        <Sidebar
                            taskConfig={props.taskConfig}
                            sidebarItems={rightSidebar.items}
                            activeItem={rightSidebar.activeItemId}
                            toggleItem={itemId => {
                                rightSidebar.changeActiveItemId(current => {
                                    const activeItem = current === itemId ? undefined : itemId
                                    if (activeItem && shouldCloseOppositeSidebar) {
                                        leftSidebar.changeActiveItemId(() => undefined)
                                    }
                                    persistUiState({
                                        rightActiveItem: activeItem ?? null,
                                    })
                                    activeItem &&
                                        notificationBadge.dismissNotificationBadge(activeItem)
                                    onRightSidebarActiveItemChangeRef.current?.(activeItem)
                                    return activeItem
                                })
                            }}
                            notificationBadges={notificationBadge.items}
                            placement="right"
                        />
                    </Sider>
                </Layout>

                <div>{modalContainer.modals.map(modal => modal.element.element)}</div>

                <Footer className={classes.bottomBar} />
            </TaskContextProvider>
        </Layout>
    )
}
